import React, { memo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const ProofTile = ({
  proof,
  isLocked,
  isNext,
  canEdit = true,
  onDelete,
  onEdit,
  onOpenDetail,
  onOpenUpload,
  onReport,
}) => {
  const [showActions, setShowActions] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const datePill = (() => {
    const d = proof?.date;
    if (!d) return '';
    const iso = String(d);
    if (iso.length >= 10 && iso[4] === '-' && iso[7] === '-') {
      return `${iso.slice(5, 7)}.${iso.slice(8, 10)}`;
    }
    return '';
  })();

  const handleTileClick = () => {
    if (isNext && canEdit) {
      onOpenUpload?.(proof?.number);
      return;
    }

    if (proof && !isLocked) {
      onOpenDetail?.(proof?.id);
    }
  };

  const handleDelete = (e) => {
    e?.stopPropagation();
    setShowDeleteConfirm(true);
  };

  const confirmDelete = (e) => {
    e?.stopPropagation();
    onDelete(proof?.id);
    setShowDeleteConfirm(false);
  };

  const handleEdit = (e) => {
    e?.stopPropagation();
    onEdit(proof);
  };

  const handleReport = (e) => {
    e?.stopPropagation();
    onReport?.(proof);
  };

  if (isLocked) {
    return (
      <div
        className="relative aspect-square bg-muted/30 rounded-lg border-2 border-dashed border-muted flex flex-col items-center justify-center p-4 transition-gaming hover:border-muted-foreground/30"
        style={{ contentVisibility: 'auto', containIntrinsicSize: '1px 240px', contain: 'layout paint' }}
      >
        <Icon name="Lock" size={32} className="text-muted-foreground mb-2" />
        <span className="text-lg md:text-xl font-bold text-muted-foreground data-text mb-1">
          #{proof?.number}
        </span>
        <span className="text-xs text-muted-foreground caption text-center">
          이전 증명 완료 필요
        </span>
      </div>
    );
  }

  if (isNext) {
    if (!canEdit) {
      return (
        <div
          className="relative aspect-square bg-muted/20 rounded-lg border border-border flex flex-col items-center justify-center p-4"
          style={{ contentVisibility: 'auto', containIntrinsicSize: '1px 240px', contain: 'layout paint' }}
        >
          <div className="w-12 h-12 md:w-16 md:h-16 bg-muted/40 rounded-md flex items-center justify-center mb-3">
            <Icon name="Lock" size={24} className="text-muted-foreground" />
          </div>
          <span className="text-lg md:text-xl font-bold text-muted-foreground data-text mb-1">
            #{proof?.number}
          </span>
          <span className="text-xs md:text-sm text-muted-foreground caption text-center font-medium">
            비공개
          </span>
        </div>
      );
    }

    return (
      <button
        onClick={handleTileClick}
        className="relative w-full aspect-square bg-card rounded-lg border border-border flex flex-col items-center justify-center p-4 transition-gaming hover:shadow-gaming-md active:translate-y-px active:shadow-gaming-sm"
        style={{ contentVisibility: 'auto', containIntrinsicSize: '1px 240px', contain: 'layout paint' }}
      >
        <div className="w-12 h-12 md:w-16 md:h-16 bg-primary/20 rounded-md flex items-center justify-center mb-3">
          <Icon name="Upload" size={24} color="var(--color-primary)" />
        </div>
        <span className="text-lg md:text-xl font-bold text-foreground data-text mb-1">
          #{proof?.number}
        </span>
        <span className="text-xs md:text-sm text-muted-foreground caption text-center font-medium">
          실행인증 하기
        </span>
      </button>
    );
  }

  return (
    <div
      className="relative aspect-square bg-card rounded-lg overflow-hidden shadow-gaming-sm transition-gaming hover:shadow-gaming-md cursor-pointer group"
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
      onClick={handleTileClick}
      style={{ contentVisibility: 'auto', containIntrinsicSize: '1px 240px', contain: 'layout paint' }}
    >
      {proof?.image ? (
        <Image
          src={proof?.image}
          alt={proof?.imageAlt}
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full bg-muted/30 flex flex-col items-center justify-center p-4">
          <div className="text-xs text-muted-foreground caption mb-2">이미지 없음</div>
          <div className="text-base md:text-lg font-heading font-bold text-foreground line-clamp-2 text-center">
            {proof?.title || '제목 없음'}
          </div>
        </div>
      )}
      <div className="absolute inset-0 bg-black/5" />
      <div className="absolute top-2 left-2 flex items-center space-x-2">
        <div className="px-2 py-1 bg-card rounded-md border border-border">
          <span className="text-sm font-bold text-foreground data-text">
            #{proof?.number}
          </span>
        </div>
        {proof?.isPublic ? (
          <div className="p-1 bg-success rounded-md">
            <Icon name="Eye" size={14} color="var(--color-success-foreground)" />
          </div>
        ) : (
          <div className="p-1 bg-muted rounded-md">
            <Icon name="EyeOff" size={14} color="var(--color-muted-foreground)" />
          </div>
        )}
      </div>
      {(datePill || (proof?.id && !String(proof.id).startsWith('placeholder-'))) ? (
        <div className="absolute top-2 right-2 z-10 flex flex-col items-end gap-2">
          {datePill ? (
            <div className="px-2.5 py-1 bg-card rounded-full border border-border">
              <span className="text-xs font-semibold text-foreground data-text">{datePill}</span>
            </div>
          ) : null}

          {proof?.id && !String(proof.id).startsWith('placeholder-') ? (
            <button
              type="button"
              onClick={handleReport}
              className="p-2 rounded-md bg-card border border-border hover:bg-card transition-gaming"
              aria-label="신고하기"
            >
              <Icon name="Flag" size={14} className="text-destructive" />
            </button>
          ) : null}
        </div>
      ) : null}
      <div className="absolute bottom-0 left-0 right-0 p-2 md:p-3">
        <div className="bg-card rounded-md border border-border p-2 md:p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-1 px-2 py-1 rounded-full bg-primary/20 border border-primary/30">
              {[...Array(5)]?.map((_, index) => (
                <Icon
                  key={index}
                  name="Star"
                  size={14}
                  color="currentColor"
                  className={index < proof?.rating ? 'fill-current text-primary' : 'text-muted-foreground'}
                />
              ))}
            </div>

            <div className="flex items-center space-x-1">
              <Icon name="Heart" size={14} color="var(--color-destructive)" className={proof?.likeCount > 0 ? 'fill-current' : ''} />
              <span className="text-xs font-semibold text-foreground data-text tabular-nums">
                {proof?.likeCount ?? 0}
              </span>
            </div>
          </div>

          {proof?.title && (
            <p className={`text-sm text-foreground mb-1 ${proof?.image ? 'font-medium line-clamp-1' : 'font-bold line-clamp-2'}`}>
              {proof?.title}
            </p>
          )}
        </div>
      </div>
      {showActions && (
        <div
          className="absolute inset-0 bg-background/95 flex items-center justify-center space-x-3 transition-gaming"
        >
          <button
            onClick={handleTileClick}
            className="w-12 h-12 bg-primary rounded-full flex items-center justify-center transition-gaming active:translate-y-px"
          >
            <Icon name="Eye" size={20} color="var(--color-primary-foreground)" />
          </button>
          {canEdit && (
            <>
              <button
                onClick={handleEdit}
                className="w-12 h-12 bg-accent rounded-full flex items-center justify-center transition-gaming active:translate-y-px"
              >
                <Icon name="Edit" size={20} color="var(--color-accent-foreground)" />
              </button>
              <button
                onClick={handleDelete}
                className="w-12 h-12 bg-destructive rounded-full flex items-center justify-center transition-gaming active:translate-y-px"
              >
                <Icon name="Trash2" size={20} color="var(--color-destructive-foreground)" />
              </button>
            </>
          )}
        </div>
      )}
      {showDeleteConfirm && (
        <div
          className="absolute inset-0 bg-background/95 flex flex-col items-center justify-center p-4 z-10"
          onClick={(e) => e?.stopPropagation()}
        >
          <Icon name="AlertTriangle" size={32} color="var(--color-warning)" className="mb-3" />
          <p className="text-sm text-center text-foreground mb-4">
            정말 삭제하시겠습니까?
          </p>
          <div className="flex items-center space-x-2">
            <button
              onClick={(e) => {
                e?.stopPropagation();
                setShowDeleteConfirm(false);
              }}
              className="px-4 py-2 bg-muted text-foreground rounded-md text-sm font-medium transition-gaming hover:bg-muted/80"
            >
              취소
            </button>
            <button
              onClick={confirmDelete}
              className="px-4 py-2 bg-destructive text-destructive-foreground rounded-md text-sm font-medium transition-gaming hover:bg-destructive/90"
            >
              삭제
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(ProofTile);